import {
  Box,
  Divider,
  Flex,
  Progress,
  Skeleton,
  Tag,
  Typography,
} from '@ergolabs/ui-kit';
import { DateTime, DurationUnits } from 'luxon';
import { Duration } from 'luxon/src/duration';
import numeral from 'numeral';
import React from 'react';
import { FC } from 'react';
import styled, { css } from 'styled-components';

import { sigUsdAsset } from '../../../common/assets/sigUsdAsset';
import { spfAsset } from '../../../common/assets/spfAsset';
import { ActiveMark } from '../../../common/components/ActiveMark/ActiveMark';
import { AssetIcon } from '../../../common/components/AssetIcon/AssetIcon';
import { CurrentRound, Round } from '../../../common/hooks/useIdoInfo';
import { useObservable } from '../../../common/hooks/useObservable';
import { getRemainingTokensByNftId } from '../../../common/services/getRemainingTokensByRound';
import { RoundTimer } from './RoundTimer/RoundTimer';

const RoundProgressBas = styled(Progress)`
  .ant-progress-outer {
    height: 24px;
    border: hidden;
    background: var(--spectrum-pool-position-bg);
    border-radius: var(--spectrum-border-radius-s);
  }
`;

const RoundProgressBarLoader = styled(Skeleton.Button)`
  .ant-skeleton-button {
    width: 100%;
    height: 24px;
    border-radius: var(--spectrum-border-radius-s);
  }
`;

export interface TimelineRoundProps {
  readonly round: Round;
  readonly now: DateTime;
  readonly currentRound?: CurrentRound;
  readonly className?: string;
  readonly showSubmittedProgress: boolean;
}

const timerUnits: DurationUnits = [
  'hours',
  'minutes',
  'seconds',
  'milliseconds',
];

const _TimelineRound: FC<TimelineRoundProps> = ({
  round,
  currentRound,
  now,
  className,
  showSubmittedProgress,
}) => {
  const [remainingTokens] = useObservable(
    getRemainingTokensByNftId(round.proxyNFTIds, round.totalAllocation),
  );

  const isWhitelistingActive = currentRound?.activeSubround === 'whitelist';
  const isContributionActive = currentRound?.activeSubround === 'contribution';

  let whitelistingDuration: Duration;
  let contributionDuration: Duration;

  if (isWhitelistingActive) {
    whitelistingDuration = round.whitelistingInterval.end.diff(now, timerUnits);
  } else if (round.passed || isContributionActive) {
    whitelistingDuration = round.whitelistingInterval.end.diff(
      round.whitelistingInterval.end,
      timerUnits,
    );
  } else {
    whitelistingDuration = round.whitelistingInterval.end.diff(
      round.whitelistingInterval.start,
      timerUnits,
    );
  }

  if (isContributionActive) {
    contributionDuration = round.contributionInterval.end.diff(now, timerUnits);
  } else if (round.passed) {
    contributionDuration = round.contributionInterval.end.diff(
      round.contributionInterval.end,
      timerUnits,
    );
  } else {
    contributionDuration = round.contributionInterval.end.diff(
      round.contributionInterval.start,
      timerUnits,
    );
  }

  const progressPercent =
    remainingTokens !== undefined
      ? numeral(round.totalAllocation)
          .subtract(remainingTokens)
          .divide(round.totalAllocation)
          .multiply(100)
          .value()
      : null;

  return (
    <Box padding={4} secondary borderRadius="l" className={className}>
      <Flex col>
        <Flex.Item marginBottom={4} display="flex" align="center">
          <Flex.Item marginRight={2}>
            <ActiveMark active={!!currentRound} />
          </Flex.Item>
          <Flex.Item marginRight={2}>
            <Typography.Title level={5}>{round.name} round</Typography.Title>
          </Flex.Item>
          {round.passed && <Tag color="success">Completed</Tag>}
        </Flex.Item>

        <Flex.Item marginBottom={4}>
          <Typography.Body secondary={!isWhitelistingActive}>
            <Flex.Item align="center">
              <AssetIcon asset={spfAsset} />
              &nbsp;1 SPF&nbsp;&nbsp;=&nbsp;&nbsp;
              <AssetIcon asset={sigUsdAsset} />
              &nbsp; {round.tokenPrice.toString(3, 0)}{' '}
              {round.tokenPrice.baseAsset.ticker}
            </Flex.Item>
          </Typography.Body>
        </Flex.Item>

        <Flex.Item marginBottom={1}>
          <Typography.Body secondary={!isWhitelistingActive}>
            <Flex align="center" justify="space-between">
              <div>Round Period: </div>
              <div>
                {round.interval.start.toFormat('d LLL')} -{' '}
                {round.interval.end.toFormat('d LLL')}
                {', '}2023
              </div>
            </Flex>
          </Typography.Body>
        </Flex.Item>

        <Flex.Item marginBottom={1}>
          <Typography.Body secondary={!isWhitelistingActive}>
            <Flex align="center" justify="space-between">
              <div>Tokens allocation: </div>
              <div>{numeral(round.totalAllocation).format('0,0.00')} SPF</div>
            </Flex>
          </Typography.Body>
        </Flex.Item>

        <Flex.Item marginBottom={1}>
          <Typography.Body secondary={!isWhitelistingActive}>
            <Flex align="center" justify="space-between">
              <div>Unlock on IDO date:</div>
              <div>{round.unlockOnIdoDate}</div>
            </Flex>
          </Typography.Body>
        </Flex.Item>

        <Flex.Item marginBottom={4}>
          <Typography.Body secondary={!isWhitelistingActive}>
            <Flex align="center" justify="space-between">
              <div>Vesting:</div>
              <div>{round.vesting}</div>
            </Flex>
          </Typography.Body>
        </Flex.Item>

        <Divider />

        <Flex.Item marginBottom={1} marginTop={4}>
          <Typography.Body strong secondary={!isWhitelistingActive}>
            <Flex align="center" justify="space-between">
              <>Whitelisting time:</>
              <RoundTimer duration={whitelistingDuration} />
            </Flex>
          </Typography.Body>
        </Flex.Item>

        <Flex.Item marginBottom={4}>
          <Typography.Body strong secondary={!isContributionActive}>
            <Flex align="center" justify="space-between">
              <>Contribution time:</>
              <RoundTimer duration={contributionDuration} />
            </Flex>
          </Typography.Body>
        </Flex.Item>

        <Divider />

        <Flex.Item marginTop={4} display="flex" col>
          <Flex.Item marginBottom={1}>
            <Typography.Body>Submitted</Typography.Body>
          </Flex.Item>
          {progressPercent !== undefined && progressPercent !== null ? (
            <RoundProgressBas
              percent={progressPercent < 0.01 ? 0 : progressPercent}
              strokeWidth={24}
            />
          ) : (
            <RoundProgressBarLoader active />
          )}
        </Flex.Item>
      </Flex>
    </Box>
  );
};

export const TimelineRound = styled(_TimelineRound)`
  ${(props) =>
    !props.currentRound &&
    css`
      opacity: 0.5;
    `}
`;
